import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { cloneDeep } from 'lodash';
import { GetComputersParams } from 'mbs-ui-shared@models/Computer';
import { ComputersAbstractService } from 'mbs-ui-shared@services/computers.service';
import { ComputersStatisticService } from 'mbs-ui-shared@services/computersStatistic.service';
import { pipe } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { ComputersStatisticState, initialComputersStatisticModel, initialComputersStatisticState } from './state';

export const ComputersStatisticStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ComputersStatisticStore'),
  withState<ComputersStatisticState>(initialComputersStatisticState),

  withComputed((store) => ({
    statisticOneComputerExistLoaded: computed(() => store.oneComputerExist() && store.oneComputerExistLoaded())
  })),

  withMethods((store) => {
    const statisticService = inject(ComputersStatisticService);
    const computersService = inject(ComputersAbstractService);

    return {
      loadStatistic: rxMethod<{ filters: GetComputersParams; force?: boolean; silent?: boolean }>(
        pipe(
          filter(({ force }) => !store.statisticLoaded() || !!force),
          mergeMap(({ silent, filters }) => {
            !silent && patchState(store, { statisticLoading: true });

            return statisticService.getComputersStatistic(filters).pipe(
              tapResponse({
                next: (data) => patchState(store, { statistic: data, statisticLoaded: true, statisticLoading: false }),
                error: () => patchState(store, { statistic: cloneDeep(initialComputersStatisticModel), statisticLoading: false })
              })
            );
          })
        )
      ),

      checkOneComputerExist: rxMethod<{ force?: boolean }>(
        pipe(
          filter(({ force }) => !store.oneComputerExistLoaded() || !!force),
          mergeMap(() => {
            patchState(store, { oneComputerExistLoading: true });

            return computersService
              .getComputers({
                withOnlineInfo: false,
                loadHidden: true,
                loadActive: true,
                includes: String.fromCharCode(32),
                limit: 1,
                offset: 0
              })
              .pipe(
                tapResponse({
                  next: (data) =>
                    patchState(store, {
                      oneComputerExist: !!data.total,
                      oneComputerExistLoading: false,
                      oneComputerExistLoaded: true
                    }),
                  error: () =>
                    patchState(store, {
                      oneComputerExist: false,
                      oneComputerExistLoading: false,
                      oneComputerExistLoaded: false
                    })
                })
              );
          })
        )
      ),

      clear(): void {
        patchState(store, cloneDeep(initialComputersStatisticState));
      }
    };
  })
);

export type ComputersStatisticStoreType = InstanceType<typeof ComputersStatisticStore>;
